import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

// Admin email audit (read-only): every email send attempt (alerts, digests,
// watchlist notifications) with recipient, subject, status and error.

const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

const STATUS_STYLE = {
  SENT: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  SKIPPED: 'bg-gray-100 text-gray-600',
};

const Pill = ({ label, value, color }) => (
  <div className="bg-white rounded-lg shadow-sm px-4 py-2">
    <span className={`text-xl font-bold ${color || 'text-gray-900'}`}>{value ?? '—'}</span>
    <span className="text-sm text-gray-500 ml-2">{label}</span>
  </div>
);

const AdminEmailLog = ({ embedded = false }) => {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await axios.get('/admin/email-log', { params: { status: status || undefined, limit: 300 } });
      setData(res.data || { rows: [], summary: {} });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load email log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(statusFilter); }, [load, statusFilter]);

  const s = data.summary || {};
  const rows = data.rows || [];

  const inner = (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          {!embedded && <h1 className="text-2xl font-bold text-gray-900">Email Audit</h1>}
          <p className="text-sm text-gray-500">Email send history (read-only)</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All statuses</option>
            <option value="SENT">Sent</option>
            <option value="FAILED">Failed</option>
            <option value="SKIPPED">Skipped</option>
          </select>
          <button onClick={() => load(statusFilter)} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded">Refresh</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <Pill label="Total" value={s.total} />
        <Pill label="Sent" value={s.sent} color="text-green-600" />
        <Pill label="Failed" value={s.failed} color="text-red-600" />
        <Pill label="Skipped" value={s.skipped} color="text-gray-500" />
        <Pill label="Last 24h" value={s.last24h} />
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading…</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600 sticky top-0">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Recipient</th>
                  <th className="px-3 py-2">Subject</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Error</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">{fmt(r.created_at)}</td>
                    <td className="px-3 py-2">{r.recipient || '—'}</td>
                    <td className="px-3 py-2 max-w-sm truncate" title={r.subject || ''}>{r.subject || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-600'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-red-600 max-w-xs truncate" title={r.error || ''}>{r.error || ''}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-500">No emails logged yet.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-2">Logging starts from deploy; older emails are not retroactively recorded.</p>
    </>
  );

  if (embedded) return inner;
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">{inner}</div>
    </div>
  );
};

export default AdminEmailLog;
